import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { professionalsAPI, specialtiesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ProfessionalsMap from '@/components/ProfessionalsMap';
import AddressSearch from '@/components/AddressSearch';
import {
  Search as SearchIcon,
  MapPin,
  Star,
  Filter,
  X,
  Stethoscope,
  Video,
  Home,
  Calendar,
  ChevronRight,
  Loader2,
  Map,
  List,
  Navigation,
  LocateFixed
} from 'lucide-react';
import { toast } from 'sonner';

const SearchProfessionals = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [professionals, setProfessionals] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [searchedAddress, setSearchedAddress] = useState(null);
  
  // Default to Yaoundé, Cameroon
  const [userLocation, setUserLocation] = useState({
    latitude: 3.8480,
    longitude: 11.5021,
  });

  const [filters, setFilters] = useState({
    specialty: searchParams.get('specialty') || '',
    serviceType: searchParams.get('type') || '',
    maxDistance: 50,
  });

  useEffect(() => {
    loadSpecialties();
  }, []);

  useEffect(() => {
    searchProfessionals();
  }, [filters, userLocation]);

  const loadSpecialties = async () => {
    try {
      const response = await specialtiesAPI.getAll();
      setSpecialties(response.data.specialties);
    } catch (error) {
      console.error('Error loading specialties:', error);
    }
  };

  const getUserLocation = () => {
    if (navigator.geolocation) {
      toast.info('Localisation en cours...');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setSearchedAddress(null);
          toast.success('Position mise à jour');
        },
        (error) => {
          toast.error('Impossible d\'accéder à votre position');
          console.log('Geolocation not available:', error);
        }
      );
    } else {
      toast.error('Géolocalisation non supportée');
    }
  };

  const handleAddressSelect = (location) => {
    if (location) {
      setUserLocation({
        latitude: location.latitude,
        longitude: location.longitude,
      });
      setSearchedAddress(location.address);
      toast.success(`Recherche autour de : ${location.address}`);
    }
  };

  const searchProfessionals = async () => {
    setLoading(true);
    try {
      const params = {
        ...(filters.specialty && { specialty: filters.specialty }),
        ...(filters.serviceType && { service_type: filters.serviceType }),
        ...(userLocation && {
          latitude: userLocation.latitude,
          longitude: userLocation.longitude,
          max_distance_km: filters.maxDistance,
        }),
      };

      const response = await professionalsAPI.search(params);
      setProfessionals(response.data.professionals);
    } catch (error) {
      console.error('Error searching professionals:', error);
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  const getServiceBadge = (services) => {
    const badges = [];
    if (services?.includes('consultation')) {
      badges.push({ icon: Calendar, label: 'Consultation', color: 'bg-[#FFEBEE] text-[#E53935]' });
    }
    if (services?.includes('video_call')) {
      badges.push({ icon: Video, label: 'Vidéo', color: 'bg-[#E3F2FD] text-[#1A237E]' });
    }
    if (services?.includes('home_visit')) {
      badges.push({ icon: Home, label: 'Domicile', color: 'bg-amber-100 text-amber-700' });
    }
    return badges;
  };

  const clearFilters = () => {
    setFilters({
      specialty: '',
      serviceType: '',
      maxDistance: 50,
    });
    setSearchParams({});
  };

  const handleProfessionalSelect = (professional) => {
    setSelectedProfessional(professional);
  };

  return (
    <div className="px-4 md:px-6 lg:px-8 py-6 w-full max-w-full overflow-x-hidden" data-testid="search-professionals-page">
      {/* Header */}
      <div className="mb-6">
        <h1 
          className="text-2xl md:text-3xl font-bold text-[#1A237E]"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Trouver un professionnel
        </h1>
        <p className="text-[#64748B] mt-1">Recherchez parmi nos professionnels de santé au Cameroun</p>
      </div>

      {/* Address Search */}
      <div className="mb-4">
        <div className="flex gap-3">
          <div className="flex-1">
            <AddressSearch
              onLocationSelect={handleAddressSelect}
              placeholder="Entrez une adresse à Yaoundé, Douala..."
              className="w-full"
            />
          </div>
          <Button
            variant="outline"
            onClick={getUserLocation}
            className="h-12 px-4 border-slate-200 rounded-xl hover:bg-[#E3F2FD] hover:border-[#2196F3]"
            title="Utiliser ma position"
            data-testid="geolocate-btn"
          >
            <LocateFixed className="w-5 h-5 text-[#2196F3]" />
          </Button>
        </div>
        {searchedAddress && (
          <p className="text-sm text-[#2196F3] mt-2 flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            Recherche autour de : {searchedAddress}
          </p>
        )}
      </div>

      {/* Search & Filters */}
      <div className="sticky top-14 md:top-16 bg-[#F8FAFC] py-4 -mx-4 px-4 md:-mx-6 md:px-6 lg:-mx-8 lg:px-8 z-30">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
            <Select
              value={filters.specialty || 'all'}
              onValueChange={(value) => setFilters({ ...filters, specialty: value === 'all' ? '' : value })}
            >
              <SelectTrigger 
                className="pl-11 h-12 bg-white border-slate-200 rounded-xl" 
                data-testid="specialty-filter"
              >
                <SelectValue placeholder="Toutes les spécialités" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les spécialités</SelectItem>
                {specialties.map((s) => (
                  <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex rounded-xl border border-slate-200 bg-white overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-3 flex items-center gap-2 transition-colors ${
                viewMode === 'list' 
                  ? 'bg-[#E53935] text-white' 
                  : 'text-[#64748B] hover:bg-slate-50'
              }`}
              data-testid="view-list-btn"
            >
              <List className="w-5 h-5" />
              <span className="hidden sm:inline text-sm font-medium">Liste</span>
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`px-4 py-3 flex items-center gap-2 transition-colors ${
                viewMode === 'map' 
                  ? 'bg-[#2196F3] text-white' 
                  : 'text-[#64748B] hover:bg-slate-50'
              }`}
              data-testid="view-map-btn"
            >
              <Map className="w-5 h-5" />
              <span className="hidden sm:inline text-sm font-medium">Carte</span>
            </button>
          </div>

          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={`h-12 px-4 border-slate-200 rounded-xl ${showFilters ? 'bg-[#FFEBEE] border-[#E53935]/30' : ''}`}
            data-testid="toggle-filters-button"
          >
            <Filter className="w-5 h-5" />
          </Button>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="mt-4 p-5 bg-white rounded-2xl border border-slate-200 shadow-sm animate-fade-in-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Filtres avancés
              </h3>
              <button 
                onClick={clearFilters} 
                className="text-sm text-[#E53935] hover:text-[#D32F2F] font-medium"
              >
                Réinitialiser
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#1A237E] mb-2 block">Type de service</label>
                <Select
                  value={filters.serviceType || 'all'}
                  onValueChange={(value) => setFilters({ ...filters, serviceType: value === 'all' ? '' : value })}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="service-type-filter">
                    <SelectValue placeholder="Tous les services" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les services</SelectItem>
                    <SelectItem value="consultation">Consultation sur place</SelectItem>
                    <SelectItem value="video_call">Téléconsultation</SelectItem>
                    <SelectItem value="home_visit">Soins à domicile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1A237E] mb-2 block">
                  Distance max : <span className="text-[#E53935]">{filters.maxDistance} km</span>
                </label>
                <input
                  type="range"
                  min="5"
                  max="100"
                  value={filters.maxDistance}
                  onChange={(e) => setFilters({ ...filters, maxDistance: parseInt(e.target.value) })}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#E53935]"
                  data-testid="distance-slider"
                />
                <div className="flex justify-between text-xs text-[#64748B] mt-1">
                  <span>5 km</span>
                  <span>100 km</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Active Filters */}
        {(filters.specialty || filters.serviceType) && (
          <div className="flex flex-wrap gap-2 mt-4">
            {filters.specialty && (
              <Badge className="bg-[#FFEBEE] text-[#E53935] gap-1 pr-1 rounded-lg">
                {filters.specialty}
                <button 
                  onClick={() => setFilters({ ...filters, specialty: '' })} 
                  className="ml-1 hover:bg-[#E53935]/20 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {filters.serviceType && (
              <Badge className="bg-[#E3F2FD] text-[#1A237E] gap-1 pr-1 rounded-lg">
                {filters.serviceType === 'consultation' ? 'Consultation' : filters.serviceType === 'video_call' ? 'Vidéo' : 'Domicile'}
                <button 
                  onClick={() => setFilters({ ...filters, serviceType: '' })} 
                  className="ml-1 hover:bg-[#2196F3]/20 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Results */}
      <div className="mt-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-10 h-10 text-[#E53935] animate-spin" />
            <p className="text-[#64748B] mt-4">Recherche en cours...</p>
          </div>
        ) : professionals.length > 0 ? (
          <>
            <p className="text-sm text-[#64748B] mb-4">
              <span className="font-semibold text-[#1A237E]">{professionals.length}</span> professionnel(s) trouvé(s)
            </p>

            {viewMode === 'map' ? (
              /* Map View */
              <div className="grid lg:grid-cols-5 gap-6">
                {/* Map */}
                <div className="lg:col-span-3">
                  <ProfessionalsMap
                    professionals={professionals}
                    center={[userLocation.latitude, userLocation.longitude]}
                    zoom={12}
                    selectedId={selectedProfessional?.id}
                    onProfessionalSelect={(p) => navigate(`/professionals/${p.id}`)}
                    height="500px"
                    className="shadow-lg"
                  />
                  <div className="mt-3 flex items-center gap-2 text-sm text-[#64748B]">
                    <Navigation className="w-4 h-4 text-[#2196F3]" />
                    <span>Cliquez sur un marqueur pour voir le profil</span>
                  </div>
                </div>
                
                {/* Side List */}
                <div className="lg:col-span-2 space-y-3 max-h-[500px] overflow-y-auto pr-2">
                  {professionals.map((professional) => (
                    <Card
                      key={professional.id}
                      className={`card-interactive border transition-all cursor-pointer ${
                        selectedProfessional?.id === professional.id 
                          ? 'border-[#2196F3] ring-2 ring-[#2196F3]/20' 
                          : 'border-slate-200'
                      }`}
                      onClick={() => {
                        setSelectedProfessional(professional);
                        navigate(`/professionals/${professional.id}`);
                      }}
                      data-testid={`map-card-${professional.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E53935] to-[#D32F2F] flex items-center justify-center text-white font-bold flex-shrink-0">
                            {professional.user?.full_name?.charAt(0) || 'D'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-[#1A237E] truncate">
                              Dr. {professional.user?.full_name}
                            </h3>
                            <p className="text-sm text-[#2196F3] font-medium">{professional.specialty}</p>
                            <div className="flex items-center gap-2 mt-2">
                              {professional.rating > 0 && (
                                <div className="flex items-center gap-1">
                                  <Star className="w-3 h-3 text-amber-400 fill-current" />
                                  <span className="text-xs font-medium">{professional.rating.toFixed(1)}</span>
                                </div>
                              )}
                              {professional.distance_km && (
                                <div className="flex items-center gap-1 text-[#64748B]">
                                  <MapPin className="w-3 h-3" />
                                  <span className="text-xs">{professional.distance_km} km</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              /* List View */
              <div className="space-y-4">
                {professionals.map((professional) => (
                  <Card
                    key={professional.id}
                    className="card-premium hover:shadow-lg transition-all cursor-pointer"
                    onClick={() => navigate(`/professionals/${professional.id}`)}
                    data-testid={`professional-card-${professional.id}`}
                  >
                    <CardContent className="p-5 md:p-6">
                      <div className="flex gap-4">
                        <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-[#E53935] to-[#D32F2F] flex items-center justify-center flex-shrink-0 shadow-lg">
                          <span className="text-white text-2xl md:text-3xl font-bold">
                            {professional.user?.full_name?.charAt(0) || 'D'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 
                                className="font-semibold text-[#1A237E] text-lg"
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                              >
                                Dr. {professional.user?.full_name}
                              </h3>
                              <p className="text-[#2196F3] font-medium">{professional.specialty}</p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-[#64748B] flex-shrink-0" />
                          </div>

                          {professional.description && (
                            <p className="text-sm text-[#64748B] mt-2 line-clamp-2">
                              {professional.description}
                            </p>
                          )}

                          <div className="flex flex-wrap items-center gap-3 mt-3">
                            {professional.rating > 0 && (
                              <div className="flex items-center gap-1 text-amber-500">
                                <Star className="w-4 h-4 fill-current" />
                                <span className="text-sm font-semibold">{professional.rating.toFixed(1)}</span>
                                <span className="text-xs text-[#64748B]">({professional.total_reviews})</span>
                              </div>
                            )}
                            {professional.distance_km && (
                              <div className="flex items-center gap-1 text-[#64748B]">
                                <MapPin className="w-4 h-4 text-[#E53935]" />
                                <span className="text-sm font-medium">{professional.distance_km} km</span>
                              </div>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-2 mt-3">
                            {getServiceBadge(professional.services).map((badge, idx) => {
                              const Icon = badge.icon;
                              return (
                                <Badge key={idx} className={`${badge.color} text-xs rounded-lg`}>
                                  <Icon className="w-3 h-3 mr-1" />
                                  {badge.label}
                                </Badge>
                              );
                            })}
                          </div>

                          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-100">
                            {professional.consultation_fee > 0 && (
                              <div className="text-sm">
                                <span className="text-[#64748B]">Consultation :</span>
                                <span className="font-bold text-[#1A237E] ml-1">
                                  {professional.consultation_fee.toLocaleString()} FCFA
                                </span>
                              </div>
                            )}
                            {professional.video_fee > 0 && (
                              <div className="text-sm">
                                <span className="text-[#64748B]">Vidéo :</span>
                                <span className="font-bold text-[#1A237E] ml-1">
                                  {professional.video_fee.toLocaleString()} FCFA
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <Stethoscope className="empty-state-icon" />
            <h3 className="empty-state-title">Aucun professionnel trouvé</h3>
            <p className="empty-state-text">
              Essayez de modifier vos critères de recherche
            </p>
            <Button
              onClick={clearFilters}
              variant="outline"
              className="mt-6 rounded-xl border-[#1A237E] text-[#1A237E] hover:bg-[#1A237E] hover:text-white"
            >
              Réinitialiser les filtres
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchProfessionals;
